import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useAuth } from '@clerk/react'
import {
  EMPTY_LABEL,
  labelAfterFileChosen,
  labelAfterOwnerEdit,
  labelAfterSuccess,
  labelAfterRefusal,
  canSubmitUpload,
  submitBlocker,
  carriedNameNotice,
  type LabelState,
} from '@/lib/upload-label'
import {
  ingestDocument,
  describeIngest,
  describeOriginal,
  fetchPendingFacts,
  approvePendingFact,
  rejectPendingFact,
  pendingDrawerLine,
  DocumentsError,
  DocumentUploadError,
  uploadDocument,
  uploadRefusalIsRecoverable,
  UPLOAD_ACCEPT_ATTRIBUTE,
  UPLOAD_LIMITS,
  VOLATILITY_CHOICES,
  certDateToEpoch,
  type PendingFact,
  type VolatilityClass,
} from '@/lib/api'
import DrawerPicker from '@/components/DrawerPicker'
import { isChosenDrawer, type DrawerChoice } from '@/lib/drawer-choice'
import { NO_SUMMARY, summaryAfterQueue, summaryAfterRead, type SummaryState } from '@/lib/intake-summary'

/**
 * DOCUMENT INTAKE — upload or paste a document, then approve what came out of it.
 *
 * MOVED HERE, NOT REWRITTEN (Kelvo build step 3c). These components were written
 * for the Foredesk owner dashboard (Builds 2a and 2b) and lived inside
 * src/routes/Dashboard.tsx. Kelvo's owner needs the same workflow on his own
 * Documents screen, so they moved out whole — the same move QuestionQueue made
 * when Kelvo's questions screen needed Foredesk's queue. Two copies of the
 * approval card would be two approval rules the day one of them changes.
 *
 * WHAT CHANGED IN THE MOVE, and nothing else did:
 *   - DocumentsSection takes an optional intro and an optional onChanged. Both
 *     absent is Foredesk, rendering exactly what it rendered before.
 *   - The upload summary appends one sentence about the stored original when the
 *     engine reports one (Kelvo only; describeOriginal returns null otherwise).
 *
 * KELVO ITEM 2 — DRAWERS. `drawers` adds a "Which drawer?" choice to the
 * form, which will not submit until one is made, and shows on each waiting
 * fact which drawer it is in. Absent is Foredesk: no picker, no drawer line.
 *
 * BATCH 2d (28 Sep 2026) — ONE CARD, NOT TWO. Upload and paste were two cards
 * with two name boxes, two drawer pickers and two buttons, and they had
 * already drifted apart in their wording. They are now one "Add a document"
 * card with an Upload a file / Paste text switch: one name box, one drawer
 * picker, one button, so there is nothing left to drift. Foredesk gets the
 * same card — it is the same component, and the same fixes.
 */

/**
 * DOCUMENTS (Build 2a) — paste paperwork, approve what came out.
 *
 * The section is TWO halves of one workflow: the paste form on top, the review
 * queue underneath, because the natural rhythm is paste → read the summary →
 * work down the list. The queue is also reachable with nothing pasted, since
 * proposals survive a page reload — they are rows, not state.
 *
 * NO ARITHMETIC AND NO WORDING LIVES HERE. The summary sentence comes from
 * describeIngest, whose honesty rules (vetoed candidates and unread chunks are
 * always reported) are what stop a short list being presented as a complete one.
 */
export function DocumentsSection({
  intro,
  onChanged,
  refreshKey,
  drawers = false,
}: {
  /**
   * The sentence under the heading. Absent means Foredesk's own wording,
   * unchanged. Kelvo passes its own, because a builder's documents are RAMS and
   * procedures, not price lists.
   */
  intro?: ReactNode
  /** Told after anything here changes what is held — an upload, an approval, a
   *  rejection — so a list elsewhere on the screen can refresh. */
  onChanged?: () => void
  /** Changes when something elsewhere on the screen changed the queue — on
   *  Kelvo, a file deleted or moved below (G52) — so it reloads. Absent
   *  (Foredesk): it never changes, and the queue loads once as before. */
  refreshKey?: number
  /** Kelvo item 2: file everything in a drawer. Absent (Foredesk): no drawers. */
  drawers?: boolean
} = {}) {
  const onChangedRef = useRef(onChanged)
  onChangedRef.current = onChanged
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [pending, setPending] = useState<PendingFact[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      const token = await getTokenRef.current()
      setPending(await fetchPendingFacts(token))
    } catch {
      setLoadError('Could not load the review queue.')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  const changed = useCallback(() => {
    void load()
    onChangedRef.current?.()
  }, [load])

  return (
    <section className="mt-8">
      <h1 className="font-display text-2xl font-semibold tracking-tight">Documents</h1>
      <p className="mt-1 text-sm text-text-muted">
        {intro ?? (
          <>
            Upload something you already have — a price list, your terms, a services page — or
            paste the text in. Either way your assistant pulls out the facts for you to check, and
            nothing goes live until you approve it.
          </>
        )}
      </p>

      <AddDocumentCard
        onIngested={changed}
        drawers={drawers}
        queueCount={pending === null ? null : pending.length}
      />

      <div className="mt-8">
        <h2 className="font-display text-xl font-semibold tracking-tight">Waiting for your say-so</h2>
        {loadError ? (
          <p className="mt-2 text-sm text-text-danger">
            {loadError}{' '}
            <button onClick={() => void load()} className="underline underline-offset-2">
              Try again
            </button>
          </p>
        ) : pending === null ? (
          <p className="mt-2 text-text-muted">Loading…</p>
        ) : pending.length === 0 ? (
          <p className="mt-2 text-text-muted">Nothing waiting. Add a document above to start.</p>
        ) : (
          <ul className="mt-4 stack-y-4">
            {pending.map((f) => (
              <li key={f.id}>
                <PendingFactCard fact={f} onDone={changed} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

/**
 * ADD A DOCUMENT — upload a file or paste its text (Builds 2a and 2b, made one
 * card in batch 2d).
 *
 * TWO WAYS IN, AND PASTING STAYS. Upload is the convenient path, not the only
 * one: the engine refuses scans and short notes by design, and a refusal is
 * only reasonable if there is something to do next. That something is the
 * Paste text side of this same card, which a recoverable refusal opens for him
 * with one press, keeping his name for the document and his drawer.
 *
 * THE NAME IS ONE BOX, WITH ITS ORIGIN. For an upload it fills itself in from
 * the file's name and he may change it — `Scan_20260830_0001.pdf` answers
 * nothing in six months. Changing it no longer touches the file: the file's own
 * name goes to the engine separately (uploadDocument's `fileName`), because
 * the label he types carries no extension and the engine reads the type from
 * the extension. That was the defect found on 28 Sep 2026.
 *
 * WHAT CLEARS AFTER A READ differs by side, deliberately. After an upload the
 * file, the name and the drawer all clear: the next file is a different
 * document and its drawer is a fresh decision. After a paste only the text
 * clears: the next paste is usually page two of the same document, in the
 * same drawer.
 */
type IntakeMode = 'upload' | 'paste'

function AddDocumentCard({
  onIngested,
  drawers,
  queueCount,
}: {
  onIngested: () => void
  drawers: boolean
  /** How many facts are waiting below; null while loading. Takes the
   *  "Found N facts" line down once he has worked through them. */
  queueCount: number | null
}) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const fileInputRef = useRef<HTMLInputElement>(null)
  const textRef = useRef<HTMLTextAreaElement>(null)

  const [mode, setMode] = useState<IntakeMode>('upload')
  const [file, setFile] = useState<File | null>(null)
  const [text, setText] = useState('')
  // The name and WHERE IT CAME FROM travel together: a string alone cannot say
  // whether he typed it or this code copied it off a file, and the two must
  // behave differently when the next file is chosen.
  const [label, setLabel] = useState<LabelState>(EMPTY_LABEL)
  const [drawer, setDrawer] = useState<DrawerChoice>('')
  const [busy, setBusy] = useState(false)
  const [summary, setSummary] = useState<SummaryState>(NO_SUMMARY)
  const [error, setError] = useState<string | null>(null)
  const [canPasteInstead, setCanPasteInstead] = useState(false)
  // The name the last paste was read under. Kept, not cleared (the next paste
  // is usually page two), and said out loud while it is still in the box.
  const [pasteCarried, setPasteCarried] = useState<string | null>(null)

  useEffect(() => {
    setSummary((current) => summaryAfterQueue(current, queueCount))
  }, [queueCount])

  const maxMb = Math.floor(UPLOAD_LIMITS.maxBytes / 1_000_000)
  const drawerReady = !drawers || isChosenDrawer(drawer)
  const canSubmit =
    mode === 'upload'
      ? canSubmitUpload(label, file !== null, busy) && drawerReady
      : label.label.trim().length > 0 && text.trim().length > 0 && !busy && drawerReady
  const blocker = canSubmit
    ? null
    : submitBlocker({
        mode,
        hasFile: file !== null,
        text,
        label: label.label,
        drawerNeeded: drawers,
        drawerChosen: isChosenDrawer(drawer),
        busy,
      })
  const carriedNotice = carriedNameNotice(mode, pasteCarried, label.label, drawers)
  // On the upload side the name and drawer appear once there is a file to
  // name and file; on the paste side they are always there.
  const showDetails = mode === 'paste' || file !== null

  function switchTo(next: IntakeMode) {
    if (next === mode) return
    setMode(next)
    setError(null)
    setCanPasteInstead(false)
  }

  function choose(picked: File | null) {
    setFile(picked)
    setSummary(NO_SUMMARY)
    setError(null)
    setCanPasteInstead(false)
    setLabel((current) => labelAfterFileChosen(current, picked?.name ?? null))
  }

  function pasteInstead() {
    // Same document, other route: his name for it and his drawer come along.
    switchTo('paste')
    // After the switch renders, so the box exists to take the focus.
    setTimeout(() => textRef.current?.focus(), 0)
  }

  async function submitUpload(f: File) {
    const token = await getTokenRef.current()
    const bytes = await f.arrayBuffer()
    const chosen = drawers && isChosenDrawer(drawer) ? drawer : undefined
    const result = await uploadDocument(
      token,
      { name: label.label.trim(), fileName: f.name, type: f.type, bytes },
      chosen,
    )
    // Kelvo adds one sentence about the file itself (kept or not, and where
    // it is filed if that is not where he just chose); Foredesk's reply
    // carries no such field, so its summary is exactly as before.
    const original = describeOriginal(result, chosen)
    setSummary(
      summaryAfterRead(original ? `${describeIngest(result)} ${original}` : describeIngest(result), result.proposed),
    )
    setFile(null)
    setLabel(labelAfterSuccess())
    setDrawer('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  async function submitPaste() {
    const token = await getTokenRef.current()
    const result = await ingestDocument(token, {
      name: label.label.trim(),
      text,
      ...(drawers && isChosenDrawer(drawer) ? { drawer } : {}),
    })
    setSummary(summaryAfterRead(describeIngest(result), result.proposed))
    setText('')
    setPasteCarried(label.label.trim())
  }

  async function submit() {
    if (!canSubmit) return
    setBusy(true)
    setError(null)
    setSummary(NO_SUMMARY)
    setCanPasteInstead(false)
    try {
      if (mode === 'upload') {
        if (!file) return
        await submitUpload(file)
      } else {
        await submitPaste()
      }
      onIngested()
    } catch (e) {
      // The name is deliberately NOT cleared on a refusal — he may want to fix
      // the file and keep his wording. What makes that safe is that its ORIGIN
      // travels with it, so an auto-filled name is replaced by the next file
      // rather than inherited by it.
      setLabel(labelAfterRefusal)
      if (e instanceof DocumentUploadError) {
        setError(e.message)
        setCanPasteInstead(uploadRefusalIsRecoverable(e.reason))
      } else if (e instanceof DocumentsError) {
        setError(e.message)
      } else {
        setError(mode === 'upload' ? 'Could not read that file.' : 'Could not read that document.')
      }
    } finally {
      setBusy(false)
    }
  }

  const tabClass = (active: boolean) =>
    `rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 disabled:opacity-50 ${
      active ? 'bg-surface-raised text-text-primary shadow-xs' : 'text-text-muted hover:text-text-primary'
    }`
  const inputClass =
    'mt-2 w-full rounded-lg border border-border-subtle bg-surface-base px-3 py-2 text-sm text-text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 disabled:opacity-50'

  return (
    <div className="mt-6 rounded-card border border-border-subtle bg-surface-raised p-5">
      <h2 className="font-display text-lg font-semibold tracking-tight text-text-primary">Add a document</h2>

      <div
        role="group"
        aria-label="How are you adding it?"
        className="mt-3 inline-flex gap-1 rounded-lg border border-border-subtle bg-surface-base p-1"
      >
        <button
          type="button"
          aria-pressed={mode === 'upload'}
          onClick={() => switchTo('upload')}
          disabled={busy}
          className={tabClass(mode === 'upload')}
        >
          Upload a file
        </button>
        <button
          type="button"
          aria-pressed={mode === 'paste'}
          onClick={() => switchTo('paste')}
          disabled={busy}
          className={tabClass(mode === 'paste')}
        >
          Paste text
        </button>
      </div>

      {/* BOTH SIDES STAY MOUNTED; the other one is hidden. Unmounting the file
          picker on a switch would empty it while this card still held the file,
          so it would read "No file chosen" over a file that would be sent. */}
      <div className="mt-4" hidden={mode !== 'upload'}>
        <label htmlFor="doc-file" className="block text-sm font-medium text-text-primary">
          The file
        </label>
        <p className="mt-0.5 text-xs text-text-muted">
          PDF, Word, Excel, CSV or plain text, up to {maxMb} MB. A scanned document or a photo of a
          page cannot be read reliably, so we will say so rather than guess at what it says.
        </p>
        <input
          ref={fileInputRef}
          id="doc-file"
          type="file"
          accept={UPLOAD_ACCEPT_ATTRIBUTE}
          disabled={busy}
          onChange={(e) => choose(e.target.files?.[0] ?? null)}
          className="mt-2 block w-full text-sm text-text-primary file:mr-3 file:rounded-lg file:border-0 file:bg-brand-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-white disabled:opacity-50"
        />
      </div>
      <div className="mt-4" hidden={mode !== 'paste'}>
        <label htmlFor="doc-text" className="block text-sm font-medium text-text-primary">
          The text
        </label>
        <p className="mt-0.5 text-xs text-text-muted">
          Open the document, copy its text, and paste it here. Handy for a short note, or a file
          that will not upload.
        </p>
        <textarea
          ref={textRef}
          id="doc-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          disabled={busy}
          className={inputClass}
        />
      </div>

      {showDetails ? (
        <div className="mt-4">
          <label htmlFor="doc-label" className="block text-sm font-medium text-text-primary">
            What is this document?
          </label>
          <p className="mt-0.5 text-xs text-text-muted">
            Every fact keeps this name, so in six months you can see exactly where it came from. Use
            the name you would use yourself.
          </p>
          <input
            id="doc-label"
            type="text"
            value={label.label}
            onChange={(e) => setLabel(labelAfterOwnerEdit(e.target.value))}
            placeholder="e.g. 2026 price list"
            maxLength={200}
            disabled={busy}
            className={inputClass}
            aria-describedby={carriedNotice ? 'doc-label-carried' : undefined}
          />
          {carriedNotice ? (
            <p id="doc-label-carried" className="mt-1 text-xs text-text-primary">
              {carriedNotice}
            </p>
          ) : null}
        </div>
      ) : null}

      {showDetails && drawers ? (
        <div className="mt-4">
          <DrawerPicker
            id="doc-drawer"
            label="Which drawer does it go in?"
            hint={
              mode === 'upload'
                ? 'This decides which of your staff can be told what is in it, and open the file.'
                : 'This decides which of your staff can be told what is in it.'
            }
            value={drawer}
            onChange={setDrawer}
            disabled={busy}
          />
        </div>
      ) : null}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!canSubmit}
          aria-busy={busy}
          aria-describedby={blocker ? 'doc-submit-blocker' : undefined}
          className="rounded-lg bg-brand-primary px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
        >
          {busy ? 'Reading…' : 'Read this document'}
        </button>
        {blocker ? (
          <p id="doc-submit-blocker" className="text-sm text-text-muted">
            {blocker}
          </p>
        ) : null}
      </div>

      {summary.summary ? (
        <p role="status" className="mt-3 text-sm text-text-primary">
          {summary.summary}
        </p>
      ) : null}
      {error ? (
        <div role="alert" className="mt-3 text-sm">
          <p className="text-text-danger">{error}</p>
          {canPasteInstead ? (
            <p className="mt-2 text-text-muted">
              Pasting the text in works just as well.{' '}
              <button
                type="button"
                onClick={pasteInstead}
                className="font-medium text-text-primary underline underline-offset-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
              >
                Paste the text instead
              </button>
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function PendingFactCard({ fact, onDone }: { fact: PendingFact; onDone: () => void }) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [volatility, setVolatility] = useState<VolatilityClass>(fact.volatilityClass)
  const [certDate, setCertDate] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const needsCertDate = volatility === 'cert_tied'
  const changed = volatility !== fact.volatilityClass
  const drawerLine = fact.drawer === undefined ? null : pendingDrawerLine(fact.drawer)

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await action()
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not save. Try again.')
    } finally {
      // Latent rather than visible: this row leaves its list on success, so the
      // stuck state unmounts before anyone reads it. Luck, not correctness — the
      // pattern is the one that bit the publish button, so it is removed rather
      // than left waiting for a list that keeps its rows.
      setBusy(false)
    }
  }

  function approve() {
    if (needsCertDate) {
      // Converted by the SHARED end-of-day helper — a certificate that runs "to
      // 15 March" is valid ON the 15th (workers/decay-format.ts has the whole
      // argument; this is the third caller and the reason it is shared).
      const seconds = certDateToEpoch(certDate)
      if (seconds === null) {
        setError('That is not a date the certificate could have.')
        return
      }
      if (seconds <= Math.floor(Date.now() / 1000)) {
        setError('That date has already passed — a lapsed certificate is not a live fact.')
        return
      }
      void run(async () => {
        await approvePendingFact(await getTokenRef.current(), fact.id, {
          volatilityClass: 'cert_tied',
          certExpiresAt: seconds,
        })
      })
      return
    }
    void run(async () => {
      await approvePendingFact(
        await getTokenRef.current(),
        fact.id,
        changed ? { volatilityClass: volatility } : undefined,
      )
    })
  }

  function reject() {
    void run(async () => {
      await rejectPendingFact(await getTokenRef.current(), fact.id)
    })
  }

  return (
    <div className="rounded-card border border-border-subtle bg-surface-raised p-4">
      <p className="text-sm text-text-primary">{fact.content}</p>
      <p className="mt-1 text-xs text-text-muted">
        {fact.sourceDoc ? `From "${fact.sourceDoc}"` : 'Proposed'}
      </p>
      {/* Kelvo: who will be told this once he signs it. Absent on Foredesk,
          whose engine sends no drawer. */}
      {drawerLine ? (
        <p
          className={`mt-1 text-xs ${drawerLine.unfiled ? 'font-medium text-text-danger' : 'text-text-muted'}`}
        >
          {drawerLine.text}
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div>
          <label
            htmlFor={`vol-${fact.id}`}
            className="block text-xs font-medium text-text-muted"
          >
            How often should we check this with you?
          </label>
          <select
            id={`vol-${fact.id}`}
            value={volatility}
            onChange={(e) => setVolatility(e.target.value as VolatilityClass)}
            disabled={busy}
            className="mt-1 rounded-lg border border-border-subtle bg-surface-base px-3 py-1.5 text-sm text-text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {VOLATILITY_CHOICES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        {needsCertDate ? (
          <div>
            <label
              htmlFor={`pcert-${fact.id}`}
              className="block text-xs font-medium text-text-muted"
            >
              Certificate expiry date
            </label>
            <input
              id={`pcert-${fact.id}`}
              type="date"
              value={certDate}
              onChange={(e) => setCertDate(e.target.value)}
              disabled={busy}
              className="mt-1 rounded-lg border border-border-subtle bg-surface-raised px-3 py-2 text-sm text-text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 disabled:opacity-50"
            />
          </div>
        ) : null}

        <button
          onClick={approve}
          disabled={busy || (needsCertDate && certDate === '')}
          aria-busy={busy}
          className="rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
        >
          {busy ? 'Saving…' : 'Approve'}
        </button>
        <button
          onClick={reject}
          disabled={busy}
          className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-danger transition-colors hover:bg-surface-base disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
        >
          Not right — remove it
        </button>
      </div>

      {error ? (
        <p role="alert" className="mt-2 text-xs text-text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}
